import React, {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Button, Tag, Typography, Tooltip, message } from 'antd';
import { StudentWithProps } from '../../../../hooks/useGetStudents';
import EnrollmentStatusMessage from '../../../../components/EnrollmentStatusMessage';
import {
  useGetStudentEnrollmentStatus,
  useCheckScheduleConflicts,
  WeekdaySelection,
} from '../../../../hooks/useGetStudentEnrollmentStatus';
import { useGet as useGetLocalSchedule } from '../../../../hooks/localSchedule/useGet';
import { useGetEnrollingSchools } from '../../../../hooks/useGetEnrollingSchools';
import {
  useCalculatePrice,
  useCalculateBulkPricing,
  convertSelectedDaysFormat,
} from '../../../../hooks/useRegistrationPricing';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import weekday from 'dayjs/plugin/weekday';
import { sanitizeHtml } from '../../../../utils/sanitizeHtml';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Text } = Typography;

const shouldPromoteForProgram = (program: any): boolean => {
  const promotionDay = dayjs(program?.promotionDay || '2024-06-01');
  const beforePromotionDay = dayjs().isBefore(promotionDay);

  if (!beforePromotionDay) {
    return false;
  }

  const referenceStartDate =
    program?.registrationWindowStartDate || program?.startDate;
  const referenceDate = dayjs(referenceStartDate);

  if (!referenceDate.isValid()) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  const referenceYear = referenceDate.year();

  return currentYear <= referenceYear;
};

/**
 * Converts a "HH:mm:ss" string into a numeric value for easy comparison.
 */
export const convertTimeToNumber = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours + minutes / 60 + (seconds || 0) / 3600;
};

/**
 * Checks if two date ranges overlap.
 */
export const dateRangeOverlaps = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): boolean => {
  const startA = Date.parse(aStart);
  const endA = Date.parse(aEnd);
  const startB = Date.parse(bStart);
  const endB = Date.parse(bEnd);

  return (
    (startA <= startB && startB <= endA) ||
    (startA <= endB && endB <= endA) ||
    (startB <= startA && endA <= endB)
  );
};

/**
 * Checks if two time ranges overlap (in the same day).
 */
export const timeRangeOverlaps = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): boolean => {
  const startA = convertTimeToNumber(aStart);
  const endA = convertTimeToNumber(aEnd);
  const startB = convertTimeToNumber(bStart);
  const endB = convertTimeToNumber(bEnd);

  return (
    (startA < startB && startB < endA) ||
    (startA < endB && endB < endA) ||
    (startB < startA && endA < endB)
  );
};

type WeekDays = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
const SUMMER_PROGRAM_TYPE_ID = 3;
const GALAXY_PROGRAM_TYPE_ID = 1;
const NOVA_PROGRAM_TYPE_ID = 4;
// One waitlist inbox covers every Nova class.
const NOVA_WAITLIST_EMAIL = 'nova@starsacramento.org';
const WEEK_DAYS: WeekDays[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

/**
 * Example concurrency discount interface. Adjust or remove if needed.
 */
interface IConcurrentDiscount {
  concurrentId: number;
  oneDay: number;
  twoDay: number;
  threeDay: number;
  fourDay: number;
  fiveDay: number;
}

/**
 * Minimal schedule shape. Adjust if needed.
 */
interface ISchedule {
  id: number;
  name: string;
  dayAndTime?: string;
  scheduleNotes?: string | null;
  programListingId?: number;
  // concurrency type: 1 => allowed, 2 => partial, 3 => disallowed if any other concurrency
  concurrent?: number;
  hidePricing?: boolean;
  fee?: number | string;
  oneDay?: number | string;
  twoDay?: number | string;
  threeDay?: number | string;
  fourDay?: number | string;
  fiveDay?: number | string;
  classSizeMax?: number;
  startTime?: string;
  endTime?: string;
  fullTime?: boolean;
  twoDayMinimum?: boolean;
  siteId?: number;

  // Booleans to indicate which days are available
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;

  // Current enrolled count for each day
  mondayEnrollment?: number;
  tuesdayEnrollment?: number;
  wednesdayEnrollment?: number;
  thursdayEnrollment?: number;
  fridayEnrollment?: number;

  gradeMinimum?: number;
  gradeMaximum?: number;
}

/**
 * How we structure the event payload for parent components.
 */
export interface SelectionPayload {
  student: StudentWithProps;
  studentId: number;
  scheduleId: number;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  totalDays: number;
  fee: number;
  pricingStatus?: 'idle' | 'loading' | 'ready' | 'error';
}

/**
 * A shape for the parent's existing selections, if needed.
 */
export interface ICurrentSelection {
  studentId?: number;
  scheduleId: number; // or string
  program?: {
    title?: string;
    group?: string;
    programTypeId?: number;
    registrationWindowId?: number;
    startDate?: string;
    endDate?: string;
  };
  schedule?: {
    concurrent?: number;
    startTime?: string;
    endTime?: string;
  };
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  days: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
  };
  pricingStatus?: 'idle' | 'loading' | 'ready' | 'error';
}

const emptyWeekdaySelection = (): Record<WeekDays, boolean> => ({
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
});

const toWeekdaySelection = (
  days: Record<WeekDays, boolean>
): WeekdaySelection => {
  const selectedDays: WeekdaySelection = {};
  (Object.keys(days) as WeekDays[]).forEach((day) => {
    if (days[day]) selectedDays[day] = true;
  });
  return selectedDays;
};

const DEFAULT_CONFLICT_MESSAGE =
  'Error checking schedule conflicts. Please try again.';

/**
 * The API rejects a conflicting selection with a 4xx and a message. A 5xx,
 * timeout or missing response means the check itself failed.
 */
export const isConflictRejection = (error: any): boolean => {
  const status =
    error?.response?.status ??
    (typeof error?.code === 'number' ? error.code : undefined);
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    ![401, 403, 408, 429].includes(status)
  );
};

const getConflictErrorMessage = (error: any): string =>
  error?.response?.data?.message ||
  (!error?.isAxiosError && typeof error?.message === 'string'
    ? error.message
    : '') ||
  DEFAULT_CONFLICT_MESSAGE;

const showConflictError = (errorMessage: string) => {
  message.error({
    content: (
      <div>
        {errorMessage.split('\n').map((line: string, index: number) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    ),
    duration: 6,
    style: { whiteSpace: 'pre-wrap' },
  });
};

const normalizeSelectionDays = (
  selection?: Partial<ICurrentSelection> | null
): Record<WeekDays, boolean> => ({
  monday: selection?.monday ?? selection?.days?.monday ?? false,
  tuesday: selection?.tuesday ?? selection?.days?.tuesday ?? false,
  wednesday: selection?.wednesday ?? selection?.days?.wednesday ?? false,
  thursday: selection?.thursday ?? selection?.days?.thursday ?? false,
  friday: selection?.friday ?? selection?.days?.friday ?? false,
});

const serializeSelectedDays = (selectedDays: Record<WeekDays, boolean>) =>
  JSON.stringify(selectedDays);

/**
 * A shape for the student's enrolled schedule from DB, if needed.
 */
interface IEnrolledSchedule {
  scheduleId: number;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  // Possibly more fields
  startTime?: string;
  endTime?: string;
  siteId?: number;
  programListing?: {
    startDate: string;
    endDate: string;
    registrationWindowId?: number;
  };
  concurrent?: number;
}

// Define the expected structure of localSchedules
interface ILocalSchedule {
  createdAt: Date;
  validTo: Date;
  selections: any[];
  schedules: ISchedule[];
}

/**
 * Props for the WeekSelector with optional concurrency features.
 */
interface IWeekSelectorProps {
  program: any; // or your type
  schedule: ISchedule;
  student: StudentWithProps;
  gradeMinimum?: { id: number; shortGrade: string };
  gradeMaximum?: { id: number; shortGrade: string };
  onChangeSelection?: (payload: SelectionPayload) => void;

  /**
   * If you want concurrency discount logic:
   */
  concurrencyDiscounts?: IConcurrentDiscount[];

  /**
   * The parent's or global list of currently selected schedules
   * that are not yet submitted. This is for concurrency conflict checks or discount logic.
   */
  currentSelections?: ICurrentSelection[];

  /**
   * The student's actually enrolled schedules from the DB, for concurrency conflict checks.
   */
  enrolledSchedules?: IEnrolledSchedule[];

  /**
   * ELOP or organizational-account checks. If the schedule is funded, you might pass them here.
   */
  organizationalAccount?: { userId: number; displayName: string };
  schedulesBilledTo?: { scheduleId: number; userId: number }[];

  /**
   * If you want to mark the schedule as fully paid by the org, so fee = 0:
   */
  isElopEligible?: boolean;

  /**
   * A function or flag that says "stop validation bubble" in the old code.
   */
  onStopValidate?: () => void;
}

/**
 * Props for the DayItem component, including disabled state and loading indicator.
 */
interface DayItemProps {
  dayLabel: string;
  dayFull: boolean;
  conflict: boolean;
  selected: boolean;
  disabled?: boolean;
  loading?: boolean;
  onToggle: () => void;
}

/**
 * Component for rendering each day button/tag in the week selector
 */
const DayItem: FC<DayItemProps> = ({
  dayLabel,
  dayFull,
  conflict,
  selected,
  disabled = false,
  loading = false,
  onToggle,
}) => {
  if (dayFull) {
    return (
      <Tooltip title="Class full">
        <Tag style={{ marginLeft: '4px', marginRight: '4px' }} color="red">
          {dayLabel}
        </Tag>
      </Tooltip>
    );
  }
  if (conflict) {
    return (
      <Tooltip title="Schedule conflict">
        <Tag style={{ marginLeft: '4px', marginRight: '4px' }} color="orange">
          {dayLabel}
        </Tag>
      </Tooltip>
    );
  }
  return (
    <Button
      size="small"
      style={{ marginLeft: '4px', marginRight: '4px' }}
      type={selected ? 'primary' : 'default'}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onToggle}
      loading={loading}
    >
      {dayLabel}
    </Button>
  );
};

/**
 * Checks if two schedules conflict by time range.
 */
const schedulesTimeConflict = (
  schedule1: {
    startTime?: string;
    endTime?: string;
  },
  schedule2: {
    startTime?: string;
    endTime?: string;
  }
): boolean => {
  const startTime1 = schedule1.startTime || '';
  const endTime1 = schedule1.endTime || '';
  const startTime2 = schedule2.startTime || '';
  const endTime2 = schedule2.endTime || '';

  if (!startTime1 || !endTime1 || !startTime2 || !endTime2) {
    return false;
  }

  return startTime1 < endTime2 && startTime2 < endTime1;
};

export const isSummerCampProgram = (programData?: { programTypeId?: number }) =>
  Number(programData?.programTypeId) === SUMMER_PROGRAM_TYPE_ID;

export const selectionHasSelectedDays = (
  selection?: Partial<ICurrentSelection>
) => {
  if (!selection) return false;
  const hasTopLevelDays = WEEK_DAYS.some((day) => Boolean(selection[day]));
  const hasNestedDays = WEEK_DAYS.some((day) => Boolean(selection.days?.[day]));
  return hasTopLevelDays || hasNestedDays;
};

export const getPricingCompanionSelections = ({
  selections,
  studentId,
  scheduleId,
}: {
  selections: Array<Partial<ICurrentSelection>>;
  studentId: number;
  scheduleId: number;
}) =>
  selections
    .filter(
      (selection) =>
        +selection.studentId === +studentId &&
        +selection.scheduleId !== +scheduleId &&
        selectionHasSelectedDays(selection)
    )
    .map((selection) => ({
      ...selection,
      days: normalizeSelectionDays(selection),
    }));

const weekRangesOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): boolean => {
  const startA = dayjs(aStart);
  const endA = dayjs(aEnd);
  const startB = dayjs(bStart);
  const endB = dayjs(bEnd);

  if (
    !startA.isValid() ||
    !endA.isValid() ||
    !startB.isValid() ||
    !endB.isValid()
  ) {
    return false;
  }

  const weekStartA = startA.startOf('week');
  const weekEndA = endA.endOf('week');
  const weekStartB = startB.startOf('week');
  const weekEndB = endB.endOf('week');

  return (
    weekStartA.isSameOrBefore(weekEndB, 'day') &&
    weekStartB.isSameOrBefore(weekEndA, 'day')
  );
};

export const hasSummerWeekConflictWithOtherSelections = ({
  currentProgram,
  currentScheduleId,
  studentId,
  selections,
}: {
  currentProgram?: {
    programTypeId?: number;
    registrationWindowId?: number;
    startDate?: string;
    endDate?: string;
  };
  currentScheduleId: number;
  studentId: number;
  selections: ICurrentSelection[];
}) => {
  if (
    !isSummerCampProgram(currentProgram) ||
    !currentProgram?.startDate ||
    !currentProgram?.endDate
  ) {
    return false;
  }

  return selections.some((selection) => {
    const sameStudent = Number(selection.studentId) === Number(studentId);
    const sameSchedule =
      Number(selection.scheduleId) === Number(currentScheduleId);
    const sameRegistrationWindow =
      currentProgram.registrationWindowId ===
      selection.program?.registrationWindowId;

    if (!sameStudent || sameSchedule || !sameRegistrationWindow) {
      return false;
    }

    if (
      !isSummerCampProgram(selection.program) ||
      !selectionHasSelectedDays(selection)
    ) {
      return false;
    }

    const selectionStartDate = selection.program?.startDate;
    const selectionEndDate = selection.program?.endDate;
    if (!selectionStartDate || !selectionEndDate) {
      return false;
    }

    return weekRangesOverlap(
      currentProgram.startDate,
      currentProgram.endDate,
      selectionStartDate,
      selectionEndDate
    );
  });
};

const getAllDatesBetweenRange = (
  weekDay: string,
  startDate: string,
  endDate: string
) => {
  const dates: string[] = [];
  const start = dayjs(startDate, 'YYYY-MM-DD');
  const end = dayjs(endDate, 'YYYY-MM-DD');

  // Normalize weekday name to capitalize the first letter
  const normalizedWeekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);

  // Map to convert weekday name to number
  const weekDayMap: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  const targetWeekDay = weekDayMap[normalizedWeekDay];

  if (targetWeekDay === undefined) {
    throw new Error(`Invalid weekDay: ${weekDay}`);
  }

  let current = start;

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    if (current.day() === targetWeekDay) {
      dates.push(current.format('YYYY-MM-DD'));
    }
    current = current.add(1, 'day');
  }

  return dates;
};

const WeekSelector: FC<IWeekSelectorProps> = ({
  program,
  schedule,
  student,
  gradeMinimum,
  gradeMaximum,
  onChangeSelection,
  currentSelections = [],
  enrolledSchedules = [],
  organizationalAccount,
  schedulesBilledTo,
  isElopEligible: isElopEligibleProp,
  onStopValidate,
}) => {
  const localSchedules = useGetLocalSchedule() as ILocalSchedule;

  // Fetch schools for email waitlist
  const { data: enrollingSchools } = useGetEnrollingSchools();

  // Find school email for waitlist
  const schoolEmail = useMemo(() => {
    if (!program?.schoolId || !enrollingSchools) return '';

    const school = enrollingSchools.find(
      (s) => s.id === Number(program.schoolId)
    );
    return school?.siteEmail || '';
  }, [program, enrollingSchools]);

  // State to track which days are selected
  const [days, setDays] = useState<Record<WeekDays, boolean>>(
    emptyWeekdaySelection
  );

  // Once the parent edits days here, stop syncing from the cart. Otherwise
  // clearing every day of a cart item would restore the cart's days.
  const hasUserEditedRef = useRef(false);

  // State to track day-level conflicts
  const [conflicts, setConflicts] = useState<Record<WeekDays, boolean>>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
  });

  // NEW: State to disable days that would cause an error/conflict on initialization
  const [dayDisabled, setDayDisabled] = useState<Record<WeekDays, boolean>>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
  });

  // Whether the schedule is ELOP-eligible or fully funded, thus zero cost
  const [isElopEligible, setIsElopEligible] = useState(false);

  // State to control ongoing conflict check
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  // State to store the calculated fee
  const [calculatedFee, setCalculatedFee] = useState(0);
  const [pricingStatus, setPricingStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [pricedDaysKey, setPricedDaysKey] = useState(() =>
    serializeSelectedDays(emptyWeekdaySelection())
  );
  const pricingRequestIdRef = useRef(0);

  // Hook for calculating pricing via backend
  const { mutateAsync: calculatePricing, isLoading: isPricingLoading } =
    useCalculatePrice();
  const { mutateAsync: calculateBulkPricing, isLoading: isBulkPricingLoading } =
    useCalculateBulkPricing();
  const calculatePricingRef = useRef(calculatePricing);
  const calculateBulkPricingRef = useRef(calculateBulkPricing);

  // Use the same hook as EnrollmentStatusMessage to check enrollment status
  const { data: enrollmentStatus } = useGetStudentEnrollmentStatus(
    student.id,
    schedule.id
  );

  // Hook for backend conflict checking
  const { mutateAsync: checkScheduleConflicts, isLoading: isConflictsLoading } =
    useCheckScheduleConflicts();

  const allLocalSelections = useMemo(
    () => [...(localSchedules?.selections || []), ...(currentSelections || [])],
    [localSchedules?.selections, currentSelections]
  );

  const companionSelections = useMemo(
    () =>
      getPricingCompanionSelections({
        selections: allLocalSelections,
        studentId: student.id,
        scheduleId: schedule.id,
      }),
    [allLocalSelections, schedule.id, student.id]
  );
  const companionSelectionsKey = useMemo(
    () =>
      JSON.stringify(
        companionSelections.map((selection) => ({
          scheduleId: selection.scheduleId,
          days: selection.days,
        }))
      ),
    [companionSelections]
  );
  const companionSelectionsRef = useRef(companionSelections);

  useEffect(() => {
    companionSelectionsRef.current = companionSelections;
  }, [companionSelections]);

  useEffect(() => {
    calculatePricingRef.current = calculatePricing;
    calculateBulkPricingRef.current = calculateBulkPricing;
  }, [calculatePricing, calculateBulkPricing]);

  const persistedSelection = useMemo(() => {
    return (
      [
        ...(currentSelections || []),
        ...(localSchedules?.selections || []),
      ].find(
        (selection) =>
          +selection.studentId === +student.id &&
          +selection.scheduleId === +schedule.id
      ) || null
    );
  }, [currentSelections, localSchedules?.selections, schedule.id, student.id]);

  useEffect(() => {
    if (!persistedSelection || hasUserEditedRef.current) {
      return;
    }

    const normalizedDays = normalizeSelectionDays(persistedSelection);
    const normalizedDaysKey = serializeSelectedDays(normalizedDays);
    const persistedSelectionCanHydrateFee =
      persistedSelection.pricingStatus === undefined ||
      persistedSelection.pricingStatus === 'idle' ||
      persistedSelection.pricingStatus === 'ready';

    setDays((currentDays) =>
      JSON.stringify(currentDays) === JSON.stringify(normalizedDays)
        ? currentDays
        : normalizedDays
    );

    if (
      persistedSelectionCanHydrateFee &&
      typeof persistedSelection.fee === 'number'
    ) {
      setCalculatedFee(persistedSelection.fee);
      setPricingStatus(
        Object.values(normalizedDays).some(Boolean)
          ? persistedSelection.pricingStatus === 'ready'
            ? 'ready'
            : 'loading'
          : 'idle'
      );
      setPricedDaysKey(normalizedDaysKey);
    }
  }, [persistedSelection]);

  const currentDaysKey = useMemo(() => serializeSelectedDays(days), [days]);

  const showConflictMessage = useCallback((text: string) => {
    message.error({
      content: (
        <div>
          <p>{text}</p>
        </div>
      ),
      duration: 6,
      style: { whiteSpace: 'pre-wrap' },
    });
  }, []);

  const isSummerWeekLocked = useMemo(
    () =>
      hasSummerWeekConflictWithOtherSelections({
        currentProgram: program,
        currentScheduleId: schedule.id,
        studentId: student.id,
        selections: allLocalSelections as ICurrentSelection[],
      }),
    [allLocalSelections, program, schedule.id, student.id]
  );

  const validateLocalSelectionForDays = useCallback(
    (daysToAdd: WeekDays[], checkOnly = false): boolean => {
      if (
        hasSummerWeekConflictWithOtherSelections({
          currentProgram: program,
          currentScheduleId: schedule.id,
          studentId: student.id,
          selections: allLocalSelections as ICurrentSelection[],
        })
      ) {
        if (checkOnly) {
          return true;
        }
        if (!checkOnly) {
          showConflictMessage(
            'This student already started another Summer Camp in the same week. Only one Summer Camp per week is allowed.'
          );
        }
        return false;
      }

      let enrolledDates: string[] = [];
      let addingDates: string[] = [];

      for (const selectedDay of daysToAdd) {
        addingDates = addingDates.concat(
          getAllDatesBetweenRange(
            selectedDay,
            program.startDate,
            program.endDate
          )
        );
      }

      for (const selection of allLocalSelections) {
        if (selection.studentId !== student.id) {
          continue;
        }

        const isCurrentSchedule = selection.scheduleId === schedule.id;
        if (isCurrentSchedule) {
          continue;
        }

        // Skip if not in the same registration window
        if (
          program.registrationWindowId !==
          selection.program?.registrationWindowId
        ) {
          continue;
        }

        // Check concurrent values - respect the most restrictive between the two schedules
        const newScheduleConcurrent = schedule.concurrent || 3; // Default to most restrictive
        const selectionConcurrent = selection.schedule?.concurrent || 3;
        const effectiveConcurrent = Math.max(
          newScheduleConcurrent,
          selectionConcurrent
        );

        // If effectiveConcurrent is 1, allow full overlap for this pair
        if (effectiveConcurrent === 1) continue;

        for (const selectedDay of daysToAdd) {
          // Check if there are time conflicts based on effective concurrent value
          if (effectiveConcurrent >= 2) {
            if (selection[selectedDay] || selection.days?.[selectedDay]) {
              // For concurrent=2, check time overlaps
              if (effectiveConcurrent === 2) {
                const timeOverlap = schedulesTimeConflict(
                  schedule,
                  selection.schedule
                );
                if (timeOverlap) {
                  enrolledDates = enrolledDates.concat(
                    getAllDatesBetweenRange(
                      selectedDay,
                      selection.program.startDate,
                      selection.program.endDate
                    )
                  );
                }
              } else if (effectiveConcurrent === 3) {
                // For concurrent=3, any day overlap is a conflict
                enrolledDates = enrolledDates.concat(
                  getAllDatesBetweenRange(
                    selectedDay,
                    selection.program.startDate,
                    selection.program.endDate
                  )
                );
              }
            }
          }
        }
      }

      for (const date of addingDates) {
        if (enrolledDates.includes(date)) {
          if (!checkOnly) {
            showConflictMessage('You are already enrolled in this day.');
          }
          return false;
        }
      }

      return true;
    },
    [allLocalSelections, program, schedule, showConflictMessage, student.id]
  );

  // Check if there's any enrollment status that prevents selection
  const hasEnrollmentRestriction = useMemo(() => {
    if (!enrollmentStatus) return false;
    return (
      enrollmentStatus.isEnrolled ||
      enrollmentStatus.isPending ||
      enrollmentStatus.hasTimeConflict
    );
  }, [enrollmentStatus]);

  // 1) GRADE ELIGIBILITY CHECK
  const isStudentGradeEligible = useMemo(() => {
    // First check if schedule has its own grade range
    if (
      schedule.gradeMinimum !== undefined &&
      schedule.gradeMaximum !== undefined
    ) {
      const studentGradeId = Number(student.gradeId);
      let scheduleMinGrade = Number(schedule.gradeMinimum);
      let scheduleMaxGrade = Number(schedule.gradeMaximum);

      const shouldPromote = shouldPromoteForProgram(program);

      // If promotion is needed, adjust grade ranges
      if (shouldPromote) {
        scheduleMinGrade -= 1;
        scheduleMaxGrade -= 1;
      }

      return (
        studentGradeId >= scheduleMinGrade && studentGradeId <= scheduleMaxGrade
      );
    }

    // Fall back to the program's grade range if schedule doesn't specify its own
    if (!gradeMinimum?.id || !gradeMaximum?.id) return true;

    const studentGradeId = Number(student.gradeId);
    let minGradeId = Number(gradeMinimum.id || 0);
    let maxGradeId = Number(gradeMaximum.id || 0);

    const shouldPromote = shouldPromoteForProgram(program);

    // If promotion is needed, adjust grade ranges
    if (shouldPromote) {
      minGradeId -= 1;
      maxGradeId -= 1;
    }

    return studentGradeId >= minGradeId && studentGradeId <= maxGradeId;
  }, [
    student.gradeId,
    gradeMinimum,
    gradeMaximum,
    schedule.gradeMinimum,
    schedule.gradeMaximum,
    program.startDate,
    program.promotionDay,
  ]);

  // 2) ELOP ELIGIBILITY CHECK
  useEffect(() => {
    if (typeof isElopEligibleProp === 'boolean') {
      setIsElopEligible(isElopEligibleProp);
      return;
    }

    if (schedulesBilledTo && organizationalAccount) {
      const found = schedulesBilledTo.find(
        (b) =>
          b.scheduleId === schedule.id &&
          b.userId === organizationalAccount.userId
      );
      if (found) {
        setIsElopEligible(true);
      }
    }
  }, [
    isElopEligibleProp,
    schedulesBilledTo,
    organizationalAccount,
    schedule.id,
  ]);

  // 4) FEE CALCULATION via Backend API
  const updatePricing = useCallback(
    async (selectedDays: Record<WeekDays, boolean>) => {
      const requestDaysKey = serializeSelectedDays(selectedDays);
      if (schedule.hidePricing || isElopEligible) {
        setCalculatedFee(0);
        setPricingStatus('ready');
        setPricedDaysKey(requestDaysKey);
        return;
      }

      const totalSelected = Object.values(selectedDays).filter(Boolean).length;
      if (totalSelected === 0) {
        setCalculatedFee(0);
        setPricingStatus('idle');
        setPricedDaysKey(requestDaysKey);
        return;
      }

      const requestId = ++pricingRequestIdRef.current;
      setPricingStatus('loading');

      try {
        // Create the current schedule request
        const currentScheduleRequest = {
          scheduleId: schedule.id,
          selectedDays: convertSelectedDaysFormat(selectedDays),
        };

        // Get all other selected schedules for this student (excluding current one)
        const otherSchedules = companionSelectionsRef.current.map(
          (selection) => ({
            scheduleId: selection.scheduleId,
            selectedDays: convertSelectedDaysFormat(selection.days),
          })
        );

        // If there are other selections, use bulk pricing to get proper concurrent discounts
        if (otherSchedules.length > 0) {
          const bulkRequest = {
            studentId: student.id,
            schedules: [currentScheduleRequest, ...otherSchedules],
          };

          const results = await calculateBulkPricingRef.current(bulkRequest);

          // Find the result for the current schedule
          const currentScheduleResult = results.find(
            (result) => result.scheduleId === schedule.id
          );

          if (currentScheduleResult) {
            if (requestId !== pricingRequestIdRef.current) {
              return;
            }
            setCalculatedFee(currentScheduleResult.price);
            setPricingStatus('ready');
            setPricedDaysKey(requestDaysKey);
          } else {
            throw new Error(
              'Could not find pricing result for current schedule'
            );
          }
        } else {
          // If no other selections, use single pricing
          const pricingRequest = {
            studentId: student.id,
            scheduleId: schedule.id,
            selectedDays: convertSelectedDaysFormat(selectedDays),
          };

          const result = await calculatePricingRef.current(pricingRequest);
          if (requestId !== pricingRequestIdRef.current) {
            return;
          }
          setCalculatedFee(result.price);
          setPricingStatus('ready');
          setPricedDaysKey(requestDaysKey);
        }
      } catch (error) {
        if (requestId !== pricingRequestIdRef.current) {
          return;
        }
        console.error('Error calculating pricing:', error);
        setCalculatedFee(0);
        setPricingStatus('error');
        setPricedDaysKey(requestDaysKey);
        message.error(
          'Unable to calculate pricing right now. Please try again.'
        );
      }
    },
    [schedule.hidePricing, schedule.id, isElopEligible, student.id]
  );

  // 5) LOCAL CONFLICT CHECK
  const checkLocalConflicts = useCallback(
    (selectedDays: Record<WeekDays, boolean>) => {
      const newConflicts = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
      };

      // Early exit if this schedule has concurrent=1 (full overlap allowed)
      if (schedule.concurrent === 1) {
        return newConflicts; // No conflicts, all overlaps allowed
      }

      // The concurrent value for the schedule we're trying to add
      const newScheduleConcurrent = schedule.concurrent || 3; // Default to most restrictive

      // Compare with enrolled schedules
      for (const enr of enrolledSchedules) {
        // Skip if not in the same registration window
        if (
          program.registrationWindowId !==
          enr.programListing?.registrationWindowId
        ) {
          continue;
        }

        // If enrolled schedule has concurrent=1, skip conflict check for this schedule
        if (enr.concurrent === 1) continue;

        // Get the most restrictive concurrent value between the two schedules
        const enrolledConcurrent = enr.concurrent || 3; // Default to most restrictive
        const effectiveConcurrent = Math.max(
          newScheduleConcurrent,
          enrolledConcurrent
        );

        // If effectiveConcurrent is 1, allow full overlap for this pair
        if (effectiveConcurrent === 1) continue;

        // Check date overlap first - if no date overlap, no conflict
        const scheduleStartDate = program.startDate;
        const scheduleEndDate = program.endDate;
        const enrolledStartDate = enr.programListing?.startDate || '';
        const enrolledEndDate = enr.programListing?.endDate || '';

        const dateOverlap = dateRangeOverlaps(
          scheduleStartDate,
          scheduleEndDate,
          enrolledStartDate,
          enrolledEndDate
        );

        if (!dateOverlap) continue;

        // Check day overlap - if no day overlap, no conflict
        let hasDayOverlap = false;
        if (selectedDays.monday && enr.monday) hasDayOverlap = true;
        if (selectedDays.tuesday && enr.tuesday) hasDayOverlap = true;
        if (selectedDays.wednesday && enr.wednesday) hasDayOverlap = true;
        if (selectedDays.thursday && enr.thursday) hasDayOverlap = true;
        if (selectedDays.friday && enr.friday) hasDayOverlap = true;

        if (!hasDayOverlap) continue;

        // If effectiveConcurrent is 2, only check time overlaps (day overlap is allowed)
        if (effectiveConcurrent === 2) {
          const timeOverlap = timeRangeOverlaps(
            schedule.startTime || '',
            schedule.endTime || '',
            enr.startTime || '',
            enr.endTime || ''
          );

          if (timeOverlap) {
            // Mark days that have overlap as conflicting
            if (selectedDays.monday && enr.monday) newConflicts.monday = true;
            if (selectedDays.tuesday && enr.tuesday)
              newConflicts.tuesday = true;
            if (selectedDays.wednesday && enr.wednesday)
              newConflicts.wednesday = true;
            if (selectedDays.thursday && enr.thursday)
              newConflicts.thursday = true;
            if (selectedDays.friday && enr.friday) newConflicts.friday = true;
          }
        } else if (effectiveConcurrent === 3) {
          // For concurrent=3, check both day and time overlap (most restrictive)
          // Mark any overlapping day as a conflict
          if (selectedDays.monday && enr.monday) newConflicts.monday = true;
          if (selectedDays.tuesday && enr.tuesday) newConflicts.tuesday = true;
          if (selectedDays.wednesday && enr.wednesday)
            newConflicts.wednesday = true;
          if (selectedDays.thursday && enr.thursday)
            newConflicts.thursday = true;
          if (selectedDays.friday && enr.friday) newConflicts.friday = true;
        }
      }

      return newConflicts;
    },
    [schedule, enrolledSchedules, program]
  );

  // 6) UPDATE PRICING WHENEVER DAYS CHANGE
  useEffect(() => {
    updatePricing(days);
  }, [days, updatePricing, companionSelectionsKey]);

  // 7) SEND CHANGES UPWARD WHENEVER `days` OR PRICING CHANGES
  useEffect(() => {
    const newConflicts = checkLocalConflicts(days);
    setConflicts(newConflicts);

    const totalDays = Object.values(days).filter(Boolean).length;
    const currentSelectionPricingStatus =
      totalDays === 0
        ? 'idle'
        : currentDaysKey === pricedDaysKey
          ? pricingStatus
          : 'loading';
    const newFee =
      currentSelectionPricingStatus === 'ready' ? calculatedFee : 0;

    // Calculate promoted grade information
    const shouldPromote = shouldPromoteForProgram(program);

    // Grade id for the program's school year. This used to be a grade id
    // when promoting but the grade label ("First") otherwise.
    const currentGradeNum =
      student.gradeId === undefined || student.gradeId === null
        ? NaN
        : Number(student.gradeId);
    const promotedGrade = isNaN(currentGradeNum)
      ? undefined
      : String(shouldPromote ? currentGradeNum + 1 : currentGradeNum);

    onChangeSelection?.({
      student: {
        ...student,
        promotedGrade,
        shouldPromote,
      },
      studentId: student.id,
      scheduleId: schedule.id,
      ...days,
      totalDays,
      fee: newFee,
      pricingStatus: currentSelectionPricingStatus,
    });

    if (schedule.twoDayMinimum && totalDays === 1 && onStopValidate) {
      onStopValidate();
    }
  }, [
    days,
    schedule,
    student.id,
    onChangeSelection,
    calculatedFee,
    currentDaysKey,
    checkLocalConflicts,
    onStopValidate,
    program.promotionDay,
    pricedDaysKey,
    pricingStatus,
    program.startDate,
    student.gradeId,
    student.Grade,
  ]);

  // 7) TOGGLE DAY (MODIFIED TO ACCEPT checkOnly)
  const toggleDay = useCallback(
    async (day: WeekDays, checkOnly = false): Promise<boolean> => {
      const isAddingDay = !days[day];

      // 7a) Local time conflict & date conflict checks
      if (isAddingDay) {
        const canAdd = validateLocalSelectionForDays([day], checkOnly);
        if (!canAdd) {
          return false;
        }
      }

      const newDays = { ...days, [day]: isAddingDay };

      // If we're only checking conflicts, skip changing state or showing messages
      if (checkOnly) {
        try {
          const result = await checkScheduleConflicts({
            studentId: student.id,
            scheduleId: schedule.id,
            selectedDays: toWeekdaySelection(newDays),
          });
          return !result?.hasTimeConflict;
        } catch (error) {
          // Only an API rejection means the day conflicts. If the check
          // itself failed, keep the day selectable; it is checked again when
          // clicked and at registration.
          return !isConflictRejection(error);
        }
      }

      // 7b) Normal path if not checkOnly
      hasUserEditedRef.current = true;
      setDays(newDays);
      if (!isAddingDay) {
        return true;
      }

      // On a conflict, undo only this day. Restoring an older snapshot would
      // also drop days picked since then.
      const revertDay = () =>
        setDays((currentDays) => ({ ...currentDays, [day]: false }));

      try {
        setIsCheckingConflicts(true);
        const result = await checkScheduleConflicts({
          studentId: student.id,
          scheduleId: schedule.id,
          selectedDays: toWeekdaySelection(newDays),
        });

        if (result?.hasTimeConflict) {
          revertDay();
          showConflictError(result.message || DEFAULT_CONFLICT_MESSAGE);
          return false;
        }
        return true;
      } catch (error) {
        console.error('Error checking conflicts for selection:', error);
        revertDay();
        showConflictError(getConflictErrorMessage(error));
        return false;
      } finally {
        setIsCheckingConflicts(false);
      }
    },
    [
      days,
      student.id,
      schedule,
      checkScheduleConflicts,
      validateLocalSelectionForDays,
    ]
  );

  // 8) TOGGLE ALL DAYS
  const toggleAllDays = useCallback(async () => {
    const newDays = {
      monday: schedule.monday ? !days.monday : days.monday,
      tuesday: schedule.tuesday ? !days.tuesday : days.tuesday,
      wednesday: schedule.wednesday ? !days.wednesday : days.wednesday,
      thursday: schedule.thursday ? !days.thursday : days.thursday,
      friday: schedule.friday ? !days.friday : days.friday,
    };

    const daysToAdd = (Object.keys(newDays) as WeekDays[]).filter(
      (day) => newDays[day] && !days[day]
    );

    if (daysToAdd.length > 0) {
      const canAdd = validateLocalSelectionForDays(daysToAdd, false);
      if (!canAdd) {
        return;
      }
    }

    // The selection as it was before this click, to restore on a conflict.
    const daysBeforeToggle = days;
    hasUserEditedRef.current = true;
    setDays(newDays);

    if (daysToAdd.length === 0) {
      return;
    }

    try {
      setIsCheckingConflicts(true);
      const result = await checkScheduleConflicts({
        studentId: student.id,
        scheduleId: schedule.id,
        selectedDays: toWeekdaySelection(newDays),
      });

      if (result?.hasTimeConflict) {
        setDays(daysBeforeToggle);
        showConflictError(result.message || DEFAULT_CONFLICT_MESSAGE);
      }
    } catch (error) {
      console.error('Error checking conflicts for complete selection:', error);
      setDays(daysBeforeToggle);
      showConflictError(getConflictErrorMessage(error));
    } finally {
      setIsCheckingConflicts(false);
    }
  }, [
    days,
    schedule,
    student.id,
    checkScheduleConflicts,
    validateLocalSelectionForDays,
  ]);

  // 9) ON COMPONENT MOUNT, CHECK WHICH DAYS WOULD FAIL AND DISABLE THEM
  useEffect(() => {
    const checkInitialDayConflicts = async () => {
      const updatedDisabled: Record<WeekDays, boolean> = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
      };

      // Only check days that are available in this schedule
      const allDays: WeekDays[] = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
      ];

      for (const d of allDays) {
        if (!schedule[d]) continue;
        // Test toggling this day. If toggleDay returns false, disable it.
        const canSelect = await toggleDay(d, true);
        if (!canSelect) {
          updatedDisabled[d] = true;
        }
      }

      // Block only the conflicting days, unless what is left can't be booked:
      // a full-time schedule is booked as a whole week, and a two-day-minimum
      // schedule needs two open days.
      const offeredDays = allDays.filter((d) => schedule[d]);
      const openDays = offeredDays.filter((d) => !updatedDisabled[d]);
      const someDaysBlocked = openDays.length < offeredDays.length;
      if (
        someDaysBlocked &&
        (schedule.fullTime || (schedule.twoDayMinimum && openDays.length < 2))
      ) {
        for (const d of offeredDays) {
          updatedDisabled[d] = true;
        }
      }

      setDayDisabled(updatedDisabled);
    };

    checkInitialDayConflicts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EARLY RETURNS
  if (!isStudentGradeEligible) {
    return null;
  }

  if (hasEnrollmentRestriction) {
    return (
      <div className="week-selector">
        <EnrollmentStatusMessage
          studentId={student.id}
          scheduleId={schedule.id}
        />
      </div>
    );
  }

  // RENDER
  const totalSelected = Object.values(days).filter(Boolean).length;
  const needsTwoDaysError = !!schedule.twoDayMinimum && totalSelected === 1;
  const isGlobalLoading =
    isCheckingConflicts ||
    isConflictsLoading ||
    isPricingLoading ||
    isBulkPricingLoading;
  const hasServerConflictBlock = Object.values(dayDisabled).some(Boolean);
  const allOfferedDaysBlocked = (WEEK_DAYS as WeekDays[])
    .filter((day) => schedule[day])
    .every((day) => dayDisabled[day]);
  const hasLocalSummerConflictBlock = isSummerWeekLocked;
  const hasAnyConflictBlock =
    hasServerConflictBlock || hasLocalSummerConflictBlock;

  const countActiveDays = () => {
    let i = 0;
    for (const day of WEEK_DAYS) {
      const dayFull =
        (schedule[`${day}Enrollment`] || 0) >=
        (schedule.classSizeMax || 999999);

      if (!dayDisabled[day as WeekDays] && !dayFull) {
        i++;
      }
    }

    return i;
  };

  // Galaxy waitlists go to the site's email and Nova's to one shared inbox.
  // Summer Camp doesn't offer a waitlist.
  const programTypeId = Number(program?.programTypeId);
  const waitlistEmail =
    programTypeId === GALAXY_PROGRAM_TYPE_ID
      ? schoolEmail
      : programTypeId === NOVA_PROGRAM_TYPE_ID
        ? NOVA_WAITLIST_EMAIL
        : '';
  const showWaitlistButton = !!waitlistEmail;
  const isSummerCamp =
    Number(program?.programTypeId) === SUMMER_PROGRAM_TYPE_ID;

  const blockedWeekSelector =
    !hasAnyConflictBlock &&
    (schedule.twoDayMinimum ? countActiveDays() < 2 : countActiveDays() < 1);
  const scheduleNotes =
    typeof schedule.scheduleNotes === 'string'
      ? schedule.scheduleNotes.trim()
      : '';

  return (
    <div
      className="week-selector"
      data-testid={`week-selector-${student.id}-${schedule.id}`}
    >
      <div style={{ border: '1px solid #ccc', marginTop: 8, padding: 8 }}>
        <div className={'text-center'}>
          <div style={{ marginBottom: '4px' }}>
            <small>
              {schedule.name}: {schedule.dayAndTime}
              {schedule.twoDayMinimum ? ' - Two Day Minimum' : ''}
            </small>
          </div>

          {isElopEligible && organizationalAccount && (
            <div style={{ marginTop: '4px', marginBottom: '4px' }}>
              <small>
                This schedule is {organizationalAccount.displayName} funded
              </small>
            </div>
          )}
        </div>

        {hasAnyConflictBlock && (
          <div className="text-center">
            <small style={{ color: 'red' }}>
              {hasLocalSummerConflictBlock
                ? 'This student already started another Summer Camp in the same week. Only one Summer Camp per week is allowed.'
                : allOfferedDaysBlocked
                  ? 'This student already has a class on this week.'
                  : "Some days aren't available because they conflict with this student's other classes."}
            </small>
          </div>
        )}

        {blockedWeekSelector ? (
          <div className="text-center mb-2">
            <p style={{ color: 'red' }} className="mb-2">
              {isSummerCamp ? 'This camp is full.' : 'This schedule is full.'}
            </p>

            {!!showWaitlistButton && (
              <Button
                type="primary"
                onClick={() => {
                  window.open(
                    `mailto:${waitlistEmail}?subject=${encodeURIComponent(
                      `Waitlist Request for ${student.firstName} ${student.lastName}`
                    )}&body=${encodeURIComponent(
                      `Please add ${student.firstName} ${student.lastName} to the waitlist for ${schedule.name}.`
                    )}`,
                    '_blank'
                  );
                }}
              >
                Email To Be Added To Waitlist
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className={'text-center'}>
              {(WEEK_DAYS as WeekDays[]).map((day) => {
                if (!schedule[day]) return null;
                const dayLabel = day.charAt(0).toUpperCase() + day.slice(1, 3);
                const dayFull =
                  (schedule[`${day}Enrollment`] || 0) >=
                  (schedule.classSizeMax || 999999);
                const loading =
                  isGlobalLoading &&
                  !dayDisabled[day] &&
                  !hasLocalSummerConflictBlock;
                const disabled =
                  isGlobalLoading ||
                  dayDisabled[day] ||
                  hasLocalSummerConflictBlock;

                return (
                  <DayItem
                    key={day}
                    dayLabel={dayLabel}
                    dayFull={dayFull}
                    conflict={conflicts[day]}
                    selected={days[day]}
                    disabled={disabled}
                    loading={loading}
                    onToggle={() => {
                      if (schedule.fullTime) {
                        toggleAllDays();
                      } else {
                        toggleDay(day);
                      }
                    }}
                  />
                );
              })}
            </div>
            {scheduleNotes && (
              <div
                className="schedule-notes text-left"
                style={{ marginTop: 8 }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(scheduleNotes),
                }}
              />
            )}
            <p
              className={'pt-1 text-center'}
              data-testid={`selected-tuition-${student.id}-${schedule.id}`}
            >
              <small>
                {program?.monthly
                  ? 'Selected Monthly Tuition:'
                  : 'Selected Tuition:'}{' '}
                {isPricingLoading || isBulkPricingLoading ? (
                  'Loading...'
                ) : pricingStatus === 'error' ? (
                  <>
                    <Text type="danger">Price unavailable.</Text>{' '}
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => updatePricing(days)}
                    >
                      Retry
                    </Button>
                  </>
                ) : (
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(calculatedFee)
                )}
              </small>
            </p>

            {needsTwoDaysError && (
              <Text
                type="danger"
                style={{ marginTop: 8, display: 'inline-block' }}
              >
                This schedule requires at least 2 days.
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WeekSelector;
